// deno-lint-ignore-file no-explicit-any
import readEmitter from "./events/read-emitter.js";
import writeEmitter, { runInAction } from "./events/write-emitter.js";
import { mapGet, setGet } from "./map-and-set.js";
const proxyMap = new WeakMap();
export const isManaged = (target) => proxyMap.has(target);
const excludeSet = new WeakSet();
export const exclude = (target) => {
    excludeSet.add(target);
    return target;
};
const reactElementSymbol = Symbol.for("react.element");
const canManage = (obj) => obj &&
    (Array.isArray(obj) ||
        obj.toString() === "[object Object]" ||
        obj.toString() === "[object Map]" ||
        obj.toString() === "[object Set]") &&
    obj["$$typeof"] !== reactElementSymbol &&
    !excludeSet.has(obj);
export const manage = (target, maxDepth = 10) => {
    if (maxDepth <= 0) {
        return target;
    }
    // return managed if it's already managed
    if (proxyMap.has(target)) {
        return proxyMap.get(target);
    }
    // return target if it cannot be managed
    if (!canManage(target)) {
        return target;
    }
    const managed = new Proxy(target, {
        // read traps
        get: (target, prop, receiver) => {
            if (target instanceof Map) {
                return mapGet(target, prop);
            }
            else if (target instanceof Set) {
                return setGet(target, prop);
            }
            const r = Reflect.get(target, prop, receiver);
            if (typeof r !== "function") {
                readEmitter.emitGet({ target, prop, value: r });
            }
            return r;
        },
        has: (target, prop) => {
            const r = Reflect.has(target, prop);
            readEmitter.emitHas({ target, prop, value: r });
            return r;
        },
        ownKeys: (target) => {
            readEmitter.emitKeys({ target });
            return Reflect.ownKeys(target);
        },
        // write traps
        defineProperty: (target, prop, descriptor) => {
            const has = Reflect.has(target, prop);
            const r = Reflect.defineProperty(target, prop, {
                ...descriptor,
                value: manage(descriptor.value, maxDepth - 1),
            });
            writeEmitter.emit({ target, prop, value: !has && r ? 1 : 0 });
            return r;
        },
        deleteProperty: (target, prop) => {
            const has = Reflect.has(target, prop);
            const r = Reflect.deleteProperty(target, prop);
            writeEmitter.emit({ target, prop, value: has && r ? -1 : 0 });
            return r;
        },
    });
    proxyMap.set(target, managed);
    proxyMap.set(managed, managed);
    // recursively manage all properties
    for (const prop of Reflect.ownKeys(target)) {
        const descriptor = Reflect.getOwnPropertyDescriptor(target, prop);
        if (descriptor.value && canManage(descriptor.value)) {
            Reflect.defineProperty(target, prop, {
                ...descriptor,
                value: manage(descriptor.value, maxDepth - 1),
            });
        }
    }
    // make all functions batched
    const seen = new Set();
    for (let current = target; current && current !== Object.prototype; current = Reflect.getPrototypeOf(current)) {
        for (const key of Reflect.ownKeys(current)) {
            if (seen.has(key))
                continue; // prototype definition should not override instance definition
            const descriptor = Reflect.getOwnPropertyDescriptor(current, key);
            if (typeof descriptor.value !== "function")
                continue;
            Reflect.defineProperty(target, key, {
                ...descriptor,
                value: Object.defineProperty(function (...args) {
                    return runInAction(() => descriptor.value.apply(this, args))[0];
                }, "name", { value: descriptor.value.name }),
            });
            seen.add(key);
        }
    }
    return managed;
};
